var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Drag-drop reorderable list of fields inside the currently active tab
 * (TASK-181 WP-B CP-2).
 *
 * Renders one row per field (delegated to `<FieldRow>` for memoization
 * per Risk R2). Selecting a row notifies the parent which displays the
 * field's property panel. Add-field button opens a minimal modal-ish
 * prompt for the tag string.
 *
 * CZNA-494 — what that prompt then INSERTS is no longer a hand-written stub.
 * It offers the tag's variants from the packaged default template (so a new
 * `550` arrives with its hidden `$w` and the right `defaultValue`, which is
 * what distinguishes a broader from a narrower term in MARC), falling back to
 * the live MARC catalog. The resolution itself is pure and lives in
 * `utils/newFieldTemplate.ts`, including why nothing here writes a `label`.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DragDropContext, Droppable, Draggable, } from '@hello-pangea/dnd';
import { Button, TextField, ConfirmationModal, RadioButton, RadioButtonGroup, } from '@folio/stripes/components';
import { useMarcCatalog } from '../../hooks/useMarcCatalog';
import FieldRow from './FieldRow';
import { readCsLabel } from './labelUtils';
import { resolveNewFieldVariants, pickDefaultVariant, } from './utils/newFieldTemplate';
var FieldListEditor = function (_a) {
    var _b, _c, _d;
    var tabIdx = _a.tabIdx, tab = _a.tab, selectedFieldIdx = _a.selectedFieldIdx, onSelectField = _a.onSelectField, dispatch = _a.dispatch, config = _a.config, packagedDefault = _a.packagedDefault;
    var intl = useIntl();
    var _e = useState(false), addOpen = _e[0], setAddOpen = _e[1];
    var _f = useState(''), newTag = _f[0], setNewTag = _f[1];
    var _g = useState(null), variantKey = _g[0], setVariantKey = _g[1];
    var _h = useState(null), confirmRemoveIdx = _h[0], setConfirmRemoveIdx = _h[1];
    var liveRegionRef = useRef(null);
    // react-query dedupes, so calling the catalog hook this deep in the tree is
    // cheap — same reasoning as IndicatorEditor, which already does it.
    var catalog = useMarcCatalog().data;
    var variants = useMemo(function () { return resolveNewFieldVariants({ tag: newTag, packaged: packagedDefault, config: config, catalog: catalog }); }, [newTag, packagedDefault, config, catalog]);
    /**
     * The variant the Add button will insert: the admin's pick when they made
     * one and it is still selectable, otherwise the computed default. Derived
     * rather than stored so editing the tag after choosing cannot leave a stale
     * selection from the previous tag's variant list.
     */
    var selectedVariant = useMemo(function () {
        var picked = variantKey ? variants.find(function (v) { return v.key === variantKey; }) : undefined;
        if (picked && !picked.alreadyPresent)
            return picked;
        return pickDefaultVariant(variants);
    }, [variants, variantKey]);
    var variantOptionLabel = function (variant) {
        var _a, _b;
        var base = variant.origin === 'PACKAGED'
            ? intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.variant.packaged' }, {
                name: variant.packagedLabel || variant.fieldId || variant.field.tag,
                count: (_b = (_a = variant.field.subfields) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
            })
            : intl.formatMessage({
                id: variant.origin === 'CATALOG'
                    ? 'ui-czna-authorities.settings.forms.visual.fields.variant.catalog'
                    : 'ui-czna-authorities.settings.forms.visual.fields.variant.bare',
            });
        if (!variant.alreadyPresent)
            return base;
        return "".concat(base, " \u2014 ").concat(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.variant.alreadyPresent' }));
    };
    var announce = useCallback(function (message) {
        if (liveRegionRef.current)
            liveRegionRef.current.textContent = message;
    }, []);
    var onDragStart = function (start) {
        var _a;
        var field = tab.fields[start.source.index];
        announce(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.drag.start' }, { tag: (_a = field === null || field === void 0 ? void 0 : field.tag) !== null && _a !== void 0 ? _a : '?' }));
    };
    var onDragUpdate = function (update) {
        if (!update.destination)
            return;
        announce(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.drag.update' }, { position: update.destination.index + 1, total: tab.fields.length }));
    };
    var onDragEnd = function (result) {
        if (!result.destination)
            return;
        var from = result.source.index;
        var to = result.destination.index;
        if (from === to)
            return;
        dispatch({ type: 'moveField', tabIdx: tabIdx, from: from, to: to });
        if (selectedFieldIdx === from)
            onSelectField(to);
        else if (selectedFieldIdx !== null) {
            if (from < selectedFieldIdx && to >= selectedFieldIdx)
                onSelectField(selectedFieldIdx - 1);
            else if (from > selectedFieldIdx && to <= selectedFieldIdx)
                onSelectField(selectedFieldIdx + 1);
        }
        announce(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.drag.end' }, { position: to + 1, total: tab.fields.length }));
    };
    var handleAddConfirm = function () {
        if (!newTag.trim() || !selectedVariant)
            return;
        dispatch({ type: 'addField', tabIdx: tabIdx, field: selectedVariant.field });
        setNewTag('');
        setVariantKey(null);
        setAddOpen(false);
        onSelectField(tab.fields.length);
    };
    var closeAddDialog = function () {
        setAddOpen(false);
        setNewTag('');
        setVariantKey(null);
    };
    var handleConfirmRemove = function () {
        if (confirmRemoveIdx === null)
            return;
        dispatch({ type: 'removeField', tabIdx: tabIdx, fieldIdx: confirmRemoveIdx });
        if (selectedFieldIdx === confirmRemoveIdx)
            onSelectField(null);
        else if (selectedFieldIdx !== null && selectedFieldIdx > confirmRemoveIdx) {
            onSelectField(selectedFieldIdx - 1);
        }
        setConfirmRemoveIdx(null);
    };
    return (React.createElement("div", { "data-testid": "field-list-editor" },
        React.createElement("div", { ref: liveRegionRef, "aria-live": "polite", "aria-atomic": "true", style: { position: 'absolute', left: '-9999px' }, "data-testid": "field-list-live-region" }),
        React.createElement("div", { style: { marginBottom: '0.5rem', fontWeight: 'bold' }, "data-testid": "field-list-active-tab-label" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.activeTabHeading", values: { label: readCsLabel(tab.label) || tab.id } })),
        React.createElement(DragDropContext, { onDragStart: onDragStart, onDragUpdate: onDragUpdate, onDragEnd: onDragEnd },
            React.createElement(Droppable, { droppableId: "field-list-".concat(tab.id) }, function (provided) { return (React.createElement("div", __assign({ ref: provided.innerRef }, provided.droppableProps),
                tab.fields.map(function (field, idx) {
                    var _a;
                    var dragId = "".concat(tab.id, "::").concat(field.tag, "::").concat((_a = field.fieldId) !== null && _a !== void 0 ? _a : idx);
                    return (React.createElement(Draggable, { key: dragId, draggableId: dragId, index: idx }, function (dragProvided) { return (React.createElement("div", __assign({ ref: dragProvided.innerRef }, dragProvided.draggableProps, { style: dragProvided.draggableProps.style }),
                        React.createElement(FieldRow, { field: field, selected: idx === selectedFieldIdx, onSelect: function () { return onSelectField(idx); }, onRemove: function () { return setConfirmRemoveIdx(idx); }, dragHandleNode: (React.createElement("span", __assign({}, dragProvided.dragHandleProps, { "data-testid": "field-row-".concat(field.tag, "-drag-handle"), style: { cursor: 'grab', color: '#999' } }), "\u283F")), removeAriaLabel: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.removeAria' }, { tag: field.tag }) }))); }));
                }),
                provided.placeholder)); })),
        React.createElement(Button, { onClick: function () { return setAddOpen(true); }, "data-testid": "field-list-add-btn", marginBottom0: true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.addField" })),
        addOpen && (React.createElement("div", { "data-testid": "field-list-add-dialog", style: { marginTop: '0.5rem', padding: '0.5rem', border: '1px dashed #999' } },
            React.createElement(TextField, { value: newTag, onChange: function (e) { return setNewTag(e.target.value); }, label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.newTagLabel' }), "data-testid": "field-list-add-tag-input" }),
            variants.length > 0 && (React.createElement("div", { style: { marginTop: '0.5rem' }, "data-testid": "field-list-add-variants" },
                React.createElement(RadioButtonGroup, { label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.fields.variant.groupLabel' }), value: (_b = selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.key) !== null && _b !== void 0 ? _b : '', onChange: function (e) { return setVariantKey(e.target.value); } }, variants.map(function (variant) { return (React.createElement(RadioButton, { key: variant.key, name: "field-list-add-variant", value: variant.key, checked: (selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.key) === variant.key, disabled: variant.alreadyPresent, label: variantOptionLabel(variant), "data-testid": "field-list-add-variant-".concat(variant.key) })); })))),
            React.createElement("div", { style: { marginTop: '0.25rem' } },
                React.createElement(Button, { onClick: handleAddConfirm, "data-testid": "field-list-add-confirm" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.addConfirm" })),
                React.createElement(Button, { onClick: closeAddDialog, "data-testid": "field-list-add-cancel" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.addCancel" }))))),
        React.createElement(ConfirmationModal, { open: confirmRemoveIdx !== null, heading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.confirmRemoveHeading" }), message: (React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.confirmRemoveMessage", values: { tag: confirmRemoveIdx !== null ? (_d = (_c = tab.fields[confirmRemoveIdx]) === null || _c === void 0 ? void 0 : _c.tag) !== null && _d !== void 0 ? _d : '' : '' } })), confirmLabel: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.confirmRemoveConfirm" }), cancelLabel: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.fields.confirmRemoveCancel" }), onConfirm: handleConfirmRemove, onCancel: function () { return setConfirmRemoveIdx(null); } })));
};
export default FieldListEditor;
